import {
  FormAsyncAutocomplete,
  FormTextField,
  Icon,
  PageContainer,
  RouteIconButtonLink,
  RouteTabLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiClipboardCheck, mdiClipboardClock, mdiEye } from "@mdi/js";
import { TabContext, TabPanel } from "@mui/lab";
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
} from "@mui/material";
import {
  queryOptions,
  useQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import z from "zod";
import type { ApiListRequest } from "../../../../@types/services/Api";
import type {
  MaterialRequestFormsFilter,
  MaterialRequestFormsOrder,
} from "../../../../@types/services/MaterialRequestForm";
import { Filter } from "../../../../components/molecules/Filter";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { useAuth } from "../../../../hooks/useAuth";
import { fetchMaterialRequestForms } from "../../../../services/materialRequestForms";
import { fetchVendors } from "../../../../services/vendors";
import { i18nextParseToDate } from "../../../../utils/i18nextParseTo";

const searchSchema = z.object({
  itemsPerPage: z.optional(z.number()).catch(25),
  order: z
    .optional(
      z.object({
        purchaseOrder: z.optional(
          z.object({
            poNumber: z.optional(z.enum(["asc", "desc"])).catch(undefined),
          }),
        ),
        updatedAt: z.optional(z.enum(["asc", "desc"])).catch(undefined),
      }),
    )
    .default({ updatedAt: "desc" })
    .catch({ updatedAt: "desc" }),
  page: z.optional(z.number()).catch(1),
  purchaseRequisition: z.optional(z.string()).catch(undefined),
  specificPurchaseOrder: z.optional(z.string()).catch(undefined),
  tab: z.optional(z.string()).catch("open"),
  vendor: z.optional(z.array(z.number())).catch(undefined),
  workOrder: z.optional(z.string()).catch(undefined),
});

const formSchema = z.object({
  purchaseRequisition: z.optional(z.string()),
  specificPurchaseOrder: z.optional(z.string()),
  vendor: z.optional(
    z.array(
      z.object({
        id: z.number(),
      }),
    ),
  ),
  workOrder: z.optional(z.string()),
});

type FormSchema = z.infer<typeof formSchema>;

const loaderQueryOptions = (
  params: ApiListRequest<MaterialRequestFormsFilter, MaterialRequestFormsOrder>,
) =>
  queryOptions({
    queryFn: () => fetchMaterialRequestForms(params),
    queryKey: ["materialRequestForms", "list", params],
    staleTime: 1000 * 60 * 5,
  });

const DataTab: React.FC = () => {
  const { tab } = Route.useSearch();

  const loaderDeps = Route.useLoaderDeps();
  const { data: materialRequestFormsResponse, isLoading } = useSuspenseQuery(
    loaderQueryOptions(loaderDeps),
  );

  const navigate = Route.useNavigate();
  const { t } = useTranslation();

  const { user } = useAuth();

  const materialRequestForms = materialRequestFormsResponse.results;
  const totalRows = useMemo(
    () => materialRequestFormsResponse.total_items,
    [materialRequestFormsResponse],
  );
  const currentPage = useMemo(
    () => materialRequestFormsResponse.current_page,
    [materialRequestFormsResponse],
  );
  const rowsPerPage = useMemo(
    () => materialRequestFormsResponse.items_per_page,
    [materialRequestFormsResponse],
  );

  // Fetch vendor details if IDs are present in URL
  const vendorIds = loaderDeps.filters.vendor || [];
  const { data: vendorData, isLoading: vendorIsLoading } = useQuery({
    enabled: vendorIds.length > 0,
    queryFn: async () => {
      if (!vendorIds.length) return [];
      // Fetch vendors by IDs - adjust this to match your API
      const response = await fetchVendors({
        filters: { id: vendorIds },
        items_per_page: vendorIds.length,
      });
      return response.results;
    },
    queryKey: ["vendors", "detail", vendorIds],
    staleTime: 1000 * 60 * 5,
  });

  const defaultValues = useMemo(() => {
    // Use fetched vendor data if available, otherwise use placeholder objects with just IDs
    const vendor =
      vendorData ||
      (loaderDeps.filters.vendor
        ? loaderDeps.filters.vendor.map((id) => ({ id }))
        : []);

    return {
      purchaseRequisition: loaderDeps.filters.purchase_requisition || "",
      specificPurchaseOrder: loaderDeps.filters.specific_purchase_order || "",
      vendor,
      workOrder: loaderDeps.filters.work_order || "",
    };
  }, [loaderDeps.filters, vendorData]);

  const activeFiltersCount = useMemo(() => {
    return Object.entries(loaderDeps.filters).filter(([key, value]) => {
      // Convert snake_case keys to camelCase for comparison with defaultValues
      const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
        letter.toUpperCase(),
      );

      return (
        camelKey in defaultValues &&
        value !== undefined &&
        value !== null &&
        !(Array.isArray(value) && value.length === 0) &&
        value !== ""
      );
    }).length;
  }, [defaultValues, loaderDeps.filters]);

  const { handleSubmit, control, reset, setValue } = useForm({
    defaultValues,
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (!vendorIsLoading) {
      setValue("vendor", defaultValues.vendor);
    }
  }, [defaultValues.vendor, setValue, vendorIsLoading]);

  const handleChangePage = useCallback(
    (
      _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
      value: number,
    ): void => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: value + 1,
        }),
      });
    },
    [navigate],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      navigate({
        search: (prev) => ({
          ...prev,
          itemsPerPage: parseInt(event.target.value, 10),
          page: 1,
        }),
      });
    },
    [navigate],
  );

  const handleFilterSubmit: SubmitHandler<FormSchema> = useCallback(
    (data) => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: 1,
          purchaseRequisition: data.purchaseRequisition,
          specificPurchaseOrder: data.specificPurchaseOrder,
          vendor: data.vendor?.map((vendor) => vendor.id),
          workOrder: data.workOrder,
        }),
      });
    },
    [navigate],
  );

  const handleFilterClear = useCallback(() => {
    navigate({
      search: (prev) => ({
        ...prev,
        page: 1,
        purchaseRequisition: undefined,
        specificPurchaseOrder: undefined,
        vendor: undefined,
        workOrder: undefined,
      }),
    });
    reset(defaultValues);
  }, [defaultValues, navigate, reset]);

  return (
    <Box>
      <Box component="form" onSubmit={handleSubmit(handleFilterSubmit)}>
        <Filter.Root
          actions={
            <Filter.Actions>
              <Filter.Action
                disabled={isLoading}
                text={t("apply")}
                type="submit"
              />
              <Filter.Action
                onClick={handleFilterClear}
                sxClass={{
                  "&:hover": { backgroundColor: "rgba(255, 0, 0, 0.1)" },
                  color: "red",
                }}
                text={t("clean")}
              />
            </Filter.Actions>
          }
          activeFiltersCount={activeFiltersCount}
        >
          <Grid size={{ md: 6, xs: 12 }}>
            <FormTextField
              control={control}
              fullWidth
              label={t("materialRequestForm.purchaseRequisition")}
              name="purchaseRequisition"
            />
          </Grid>
          {tab === "finished" && (
            <Grid size={{ md: 6, xs: 12 }}>
              <FormTextField
                control={control}
                fullWidth
                label={t("materialRequestForm.purchaseOrder")}
                name="specificPurchaseOrder"
              />
            </Grid>
          )}
          <Grid size={{ md: 6, xs: 12 }}>
            <FormTextField
              control={control}
              fullWidth
              label={t("materialRequestForm.workOrder")}
              name="workOrder"
            />
          </Grid>
          {!!user?.equinor && (
            <Grid size={{ md: 6, xs: 12 }}>
              <FormAsyncAutocomplete
                control={control}
                fetchOptions={fetchVendors}
                filters={(searchTerm: string) => ({
                  active: true,
                  blocked: false,
                  material_request_form_permission: true,
                  name: searchTerm,
                })}
                getOptionLabel={(option) =>
                  option?.display_name_with_sap_code_and_tax_number ?? ""
                }
                label={t("materialRequestForm.vendor")}
                minSearchLength={2}
                multiple
                name="vendor"
                queryKey={["materialRequestForm", "vendor", "list"]}
                size="medium"
              />
            </Grid>
          )}
        </Filter.Root>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell align="left">
                {t("materialRequestForm.purchaseRequisition")}
              </TableCell>
              <TableCell align="left">
                {t("materialRequestForm.requisitionDate")}
              </TableCell>
              <TableCell align="left">
                {t("materialRequestForm.mainWorkOrder")}
              </TableCell>
              <TableCell align="left">
                {t("materialRequestForm.vendor")}
              </TableCell>
              <TableCell align="left">
                {t("materialRequestForm.purchaseOrder")}
              </TableCell>
              <TableCell align="center">
                {t("materialRequestForm.filled")}
              </TableCell>
              <TableCell align="center" />
            </TableRow>
          </TableHead>
          <TableBody>
            {!!isLoading && (
              <TableRow key="loading-skeleton">
                {Array(7).map(() => (
                  <TableCell key={`skeleton-cell-${crypto.randomUUID()}`}>
                    <Skeleton />
                  </TableCell>
                ))}
              </TableRow>
            )}
            {!isLoading &&
              materialRequestForms.length > 0 &&
              materialRequestForms.map((materialRequestForm) => (
                <TableRow key={materialRequestForm.id}>
                  <TableCell>
                    {materialRequestForm.purchase_requisition_number}
                  </TableCell>
                  <TableCell>
                    <span>
                      {materialRequestForm.material_request_form_items?.length >
                        0 &&
                        materialRequestForm.material_request_form_items[0] &&
                        t(
                          "intlDate",
                          i18nextParseToDate(
                            materialRequestForm.material_request_form_items[0]
                              .purchase_requisition.requisition_date,
                          ),
                        )}
                    </span>
                  </TableCell>
                  <TableCell>
                    {materialRequestForm.material_request_form_items?.length >
                      0 &&
                      materialRequestForm.material_request_form_items[0] &&
                      materialRequestForm.material_request_form_items[0]
                        .purchase_requisition.account_assignment_category ===
                        "F" &&
                      materialRequestForm.material_request_form_items[0]
                        .purchase_requisition.plant_maintenance_order &&
                      `${materialRequestForm.material_request_form_items[0].purchase_requisition.plant_maintenance_order.order_number} - ${materialRequestForm.material_request_form_items[0].purchase_requisition.plant_maintenance_order.description}`}
                  </TableCell>
                  <TableCell>
                    {materialRequestForm.vendor.fullvendorname}
                  </TableCell>
                  <TableCell>
                    {materialRequestForm.purchase_order
                      ? materialRequestForm.purchase_order.po_number
                      : materialRequestForm.create_purchase_order_jid && (
                          <span>
                            <CircularProgress
                              color="success"
                              size={16}
                              sx={{ marginRight: "5px" }}
                            />
                            <span>{t("materialRequestForm.generating")}</span>
                          </span>
                        )}
                  </TableCell>
                  <TableCell align="center">
                    {materialRequestForm.filled ? (
                      <Tooltip title={t("yes")}>
                        <Icon color="success" icon={mdiClipboardCheck} />
                      </Tooltip>
                    ) : (
                      <Tooltip title={t("no")}>
                        <Icon color="info" icon={mdiClipboardClock} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t("view")}>
                      <RouteIconButtonLink
                        params={{
                          materialRequestFormId: materialRequestForm.id,
                        }}
                        to="/vbuyer/material_request_forms/$materialRequestFormId"
                      >
                        <Icon icon={mdiEye} />
                      </RouteIconButtonLink>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                count={totalRows}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                page={currentPage - 1}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                showFirstButton
                showLastButton
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
};

const RouteComponent: React.FC = () => {
  const loaderDeps = Route.useLoaderDeps();
  const { t } = useTranslation();

  const tabOpen = useMemo(
    () => (loaderDeps.filters.has_purchase_order ? "finished" : "open"),
    [loaderDeps.filters.has_purchase_order],
  );

  return (
    <PageContainer maxWidth="xl" title={t("materialRequestForm.title")}>
      <TabContext value={tabOpen}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabOpen}>
            <RouteTabLink
              label={t("materialRequestForm.open")}
              search={{ order: { updatedAt: "desc" }, tab: "open" }}
              to="/vbuyer/material_request_forms"
              value="open"
            />
            <RouteTabLink
              label={t("materialRequestForm.finished")}
              search={{
                order: { purchaseOrder: { poNumber: "desc" } },
                tab: "finished",
              }}
              to="/vbuyer/material_request_forms"
              value="finished"
            />
          </Tabs>
        </Box>

        <TabPanel value="open">
          <DataTab />
        </TabPanel>
        <TabPanel value="finished">
          <DataTab />
        </TabPanel>
      </TabContext>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/material_request_forms/",
  // biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
)({
  validateSearch: searchSchema,
  component: RouteComponent,
  loaderDeps: ({ search }) => ({
    filters: {
      has_purchase_order: search.tab === "finished",
      purchase_requisition: search.purchaseRequisition,
      specific_purchase_order: search.specificPurchaseOrder,
      vendor: search.vendor,
      work_order: search.workOrder,
    },
    items_per_page: search.itemsPerPage,
    orders: {
      "order[purchase_order.po_number]": search.order.purchaseOrder?.poNumber,
      "order[updated_at]": search.order.updatedAt,
    },
    page: search.page,
  }),
  beforeLoad({ context }) {
    if (
      context.auth.user?.roles_names.includes("Material Request Form - View")
    ) {
      return;
    }

    throw new ForbiddenError();
  },
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(loaderQueryOptions(deps)),
});
